import { DeleteOutlined, EyeOutlined, SearchOutlined } from '@ant-design/icons';
import {
  Button,
  Card,
  Col,
  Input,
  Popconfirm,
  Radio,
  Row,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { format } from 'date-fns';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CustomerResponse from '../../model/Customer.model';
import CustomerService from '../../service/customer-service/Customer.service';
import EditCustomerModal from './EditCustomerModal';

interface CustomerListTableProps {
  customerList: CustomerResponse[];
  onSuccess: () => Promise<void>;
}

const CustomerListTable: React.FC<CustomerListTableProps> = ({
  customerList,
  onSuccess,
}) => {
  const navigate = useNavigate();

  const [searchQuery, setSearchQuery] = useState('');
  const [partnerSearch, setPartnerSearch] = useState('');
  const [phoneSearch, setPhoneSearch] = useState('');

  const handleDelete = async (id: number) => {
    try {
      await CustomerService.deleteCustomer(id);
      onSuccess();
    } catch (error) {
      console.error('Error deleting customer:', error);
    }
  };

  const filteredCustomers = customerList
    .filter((customer) => {
      // Lọc theo search khách hàng
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const customerMatch =
          (customer.name?.toLowerCase() || '').includes(query) ||
          (customer.identificationNumber?.toLowerCase() || '').includes(
            query
          ) ||
          (customer.email?.toLowerCase() || '').includes(query);
        if (!customerMatch) return false;
      }

      // Lọc theo search đối tác
      if (partnerSearch) {
        const partnerQuery = partnerSearch.toLowerCase();
        const partnerMatch =
          (customer.partnerName?.toLowerCase() || '').includes(partnerQuery) ||
          (customer.partnerCode?.toLowerCase() || '').includes(partnerQuery);
        if (!partnerMatch) return false;
      }

      // Lọc theo số điện thoại
      if (phoneSearch) {
        const phoneQuery = phoneSearch.toLowerCase();
        const phoneMatch = (customer.contactInfo?.toLowerCase() || '').includes(
          phoneQuery
        );
        if (!phoneMatch) return false;
      }

      return true;
    })
    .sort((a, b) => {
      // Sắp xếp theo ngày tạo mới nhất lên đầu (createdAt descending)
      const dateA = new Date(a.createdAt).getTime();
      const dateB = new Date(b.createdAt).getTime();
      return dateB - dateA; // Descending order - mới nhất lên đầu
    });

  const columns: ColumnsType<CustomerResponse> = [
    {
      title: 'Tên khách hàng',
      dataIndex: 'name',
      key: 'name',
      width: 200,
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: 'Tổng số thẻ',
      dataIndex: 'cardAmount',
      key: 'cardAmount',
      width: 100,
      responsive: ['md'],
    },
    {
      title: 'Số điện thoại',
      dataIndex: 'contactInfo',
      key: 'contactInfo',
      responsive: ['sm'],
      filterDropdown: () => (
        <div className="p-2" style={{ minWidth: '200px' }}>
          <Input
            placeholder="Tìm kiếm số điện thoại"
            value={phoneSearch}
            onChange={(e) => setPhoneSearch(e.target.value)}
            style={{ width: '100%', marginBottom: 8 }}
            allowClear
          />
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      ),
      filtered: !!phoneSearch,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      responsive: ['lg'],
    },
    {
      title: 'Số CCCD/CMT',
      dataIndex: 'identificationNumber',
      key: 'identificationNumber',
      responsive: ['md'],
    },
    {
      title: 'Ngày đăng ký',
      dataIndex: 'registrationDate',
      key: 'registrationDate',
      responsive: ['md'],
      render: (date: string) => format(new Date(date), 'dd-MM-yyyy'),
      sorter: (a, b) =>
        new Date(a.registrationDate).getTime() -
        new Date(b.registrationDate).getTime(),
    },
    {
      title: 'Người tạo',
      dataIndex: 'createdByName',
      key: 'createdById',
      responsive: ['md'],
      render: (createdBy: string) => createdBy || '—',
    },
    {
      title: 'Đối tác',
      key: 'partner',
      responsive: ['lg'],
      render: (_, record) =>
        record.partnerId ? (
          <Tooltip title={`Mã: ${record.partnerCode}`}>
            <Tag color="blue" className="cursor-help">
              {record.partnerName} - {record.partnerCode}
            </Tag>
          </Tooltip>
        ) : (
          '—'
        ),
      filters: [
        { text: 'Có đối tác', value: 'hasPartner' },
        { text: 'Không có đối tác', value: 'noPartner' },
      ],
      onFilter: (value, record) =>
        value === 'hasPartner' ? !!record.partnerId : !record.partnerId,
      filterDropdown: () => (
        <div className="p-2" style={{ minWidth: '200px' }}>
          <Input
            placeholder="Tìm kiếm đối tác"
            value={partnerSearch}
            onChange={(e) => setPartnerSearch(e.target.value)}
            style={{ width: '100%', marginBottom: 8 }}
            allowClear
          />
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      ),
      filtered: !!partnerSearch,
    },
    {
      title: 'Ghi chú',
      dataIndex: 'note',
      key: 'note',
      width: 200,
      responsive: ['xl'],
      render: (note: string) =>
        note ? (
          <Tooltip title={note} placement="topLeft">
            <Typography.Text
              ellipsis={{ tooltip: false }}
              style={{ maxWidth: 200 }}
              className="cursor-help"
            >
              {note}
            </Typography.Text>
          </Tooltip>
        ) : (
          '—'
        ),
    },
    {
      title: 'Hành động',
      key: 'action',
      width: 120,
      fixed: 'right',
      render: (_, record) => (
        <Space size={0} className="flex flex-wrap justify-center">
          <EditCustomerModal customer={record} onSuccess={onSuccess} />

          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => navigate(`/customer/${record.id}`)}
            title="Chi tiết"
            className="flex items-center justify-center"
          />

          <Popconfirm
            title="Xác nhận xóa"
            description="Bạn có chắc chắn muốn xóa khách hàng này?"
            onConfirm={() => handleDelete(record.id)}
            okText="Xóa"
            cancelText="Hủy"
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              title="Xóa"
              className="flex items-center justify-center"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const getSearchPlaceholder = () => {
    return 'Tìm kiếm theo tên, CCCD/CMT, email';
  };

  return (
    <Card className="shadow-sm">
      <Row gutter={[16, 16]} className="mb-4">
        <Col xs={24} md={16} lg={12}>
          <Tooltip title={getSearchPlaceholder()}>
            <Input
              placeholder={getSearchPlaceholder()}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ width: '100%' }}
              prefix={<SearchOutlined />}
              allowClear
            />
          </Tooltip>
        </Col>
        <Col xs={24} md={8} lg={6}>
          <Tooltip title="Tìm kiếm theo số điện thoại">
            <Input
              placeholder="Tìm theo số điện thoại"
              value={phoneSearch}
              onChange={(e) => setPhoneSearch(e.target.value)}
              style={{ width: '100%' }}
              prefix={<SearchOutlined />}
              allowClear
            />
          </Tooltip>
        </Col>
      </Row>

      <Table
        columns={columns}
        dataSource={filteredCustomers}
        rowKey="id"
        pagination={{
          pageSize: 10,
          showTotal: (total) => `Tổng cộng ${total} khách hàng`,
          showSizeChanger: false,
          position: ['bottomRight'],
        }}
        scroll={{ x: 'max-content' }}
        bordered
        size="middle"
      />
    </Card>
  );
};

export default CustomerListTable;
